'use client';

/**
 * ThemeEditor — Live theme editor MVP per Nicola (owner).
 *
 * Architettura:
 *  - Floating toggle button bottom-left (visibile solo se props.enabled === true)
 *  - Click "Pick" → enter pick-mode: hover highlights elements, click seleziona
 *  - Selected element: bounding box overlay + 8 resize handles (4 corner + 4 edge)
 *  - Side panel destra: tabs Size / Spacing / Color / Typography / Raw CSS
 *  - Modifiche applicate REALTIME via element.style + tracciate in state
 *  - "Salva" → POST /api/theme-editor/save (JSON: selectorPath → CSS props)
 *
 * Storage path: selettore CSS robusto (cssPath) calcolato per ogni elemento.
 *
 * NOTA: questo è MVP. Iterazioni successive: resize drag handles, copy CSS, undo/redo.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Overrides format esteso per per-breakpoint:
//   { "[data-cid='x']": { "color": "#fff",                          // base scope
//                          "@md": { "color": "#000" },               // md+ override
//                          "@lg": { "font-size": "24px" } } }
// Chiavi `@<bp>` sono nested record di prop→value. Base resta flat al primo livello.
type Overrides = Record<string, Record<string, string | Record<string, string>>>;
type BpKey = 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
const BP_MIN: Record<Exclude<BpKey, 'base'>, number> = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };

interface Props {
  initialOverrides: Overrides;
}

const PANEL_TABS = ['Size', 'Spacing', 'Color', 'Typography', 'Attrs', 'Raw'] as const;
type Tab = typeof PANEL_TABS[number];

// Mappa property CSS → Tailwind utility prefix (Tailwind 3+ arbitrary values syntax).
const TW_MAP: Record<string, string> = {
  background: 'bg', 'background-color': 'bg',
  color: 'text',
  padding: 'p', 'padding-top': 'pt', 'padding-right': 'pr', 'padding-bottom': 'pb', 'padding-left': 'pl',
  margin: 'm', 'margin-top': 'mt', 'margin-right': 'mr', 'margin-bottom': 'mb', 'margin-left': 'ml',
  width: 'w', height: 'h', 'max-width': 'max-w', 'min-width': 'min-w', 'max-height': 'max-h', 'min-height': 'min-h',
  'border-radius': 'rounded', 'border-color': 'border',
  'box-shadow': 'shadow',
  'font-size': 'text', 'font-weight': 'font', 'line-height': 'leading', 'letter-spacing': 'tracking',
  'text-align': 'text',
  gap: 'gap', 'row-gap': 'gap-y', 'column-gap': 'gap-x',
  opacity: 'opacity',
  display: '', 'flex-direction': '', 'justify-content': '', 'align-items': '',
};

function toTailwindClass(prop: string, value: string): string {
  const v = value.trim().replace(/\s+/g, '_'); // Tailwind arbitrary values: no spazi, usa _
  const prefix = TW_MAP[prop];
  if (prefix === undefined) return ''; // prop non mappata
  if (prefix === '') return ''; // prop con valore Tailwind non arbitrary-syntax-friendly (display, flex-direction)
  return `${prefix}-[${v}]`;
}

// Genera blocco testo per export pronto da copiare.
function generateExport(overrides: Overrides): string {
  const lines: string[] = [];
  for (const [sel, props] of Object.entries(overrides)) {
    const baseClasses: string[] = [];
    const bpClasses: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(props)) {
      if (k.startsWith('@') && typeof v === 'object') {
        const bp = k.slice(1);
        for (const [pk, pv] of Object.entries(v as Record<string, string>)) {
          const cls = toTailwindClass(pk, pv);
          if (cls) (bpClasses[bp] ||= []).push(`${bp}:${cls}`);
        }
      } else if (typeof v === 'string') {
        const cls = toTailwindClass(k, v);
        if (cls) baseClasses.push(cls);
      }
    }
    const all = [...baseClasses];
    for (const bp of Object.keys(bpClasses)) all.push(...bpClasses[bp]);
    if (all.length === 0) continue;
    lines.push(`/* ${sel} */`);
    lines.push(all.join(' '));
    lines.push('');
  }
  return lines.join('\n').trim() || '/* nessuna prop mappabile in Tailwind */';
}

// Restituisce lista ancestors dell'elemento (incluso self), dal più alto (body) al più basso (self).
function getAncestors(el: HTMLElement): HTMLElement[] {
  const chain: HTMLElement[] = [];
  let cur: HTMLElement | null = el;
  while (cur && cur !== document.body && cur !== document.documentElement) {
    chain.unshift(cur);
    cur = cur.parentElement;
  }
  return chain;
}

// Label sintetica per breadcrumb (data-cid → #id → tag[.first-class])
function ancestorLabel(el: HTMLElement): string {
  const cid = el.getAttribute('data-cid');
  if (cid) return cid;
  if (el.id) return `#${el.id}`;
  const tag = el.tagName.toLowerCase();
  const cls = el.className && typeof el.className === 'string'
    ? el.className.split(/\s+/).find((c) => c && !c.startsWith('hover:') && !c.startsWith('focus:'))
    : '';
  return cls ? `${tag}.${cls}` : tag;
}

// Calcola un selettore CSS unico ma stabile per l'elemento (preferisce data-cid → id → nth-child path).
function cssPath(el: Element): string {
  if (!el || el.nodeType !== 1) return '';
  const cid = el.getAttribute('data-cid');
  if (cid) return `[data-cid="${cid}"]`;
  if (el.id) return `#${el.id}`;
  const segments: string[] = [];
  let cur: Element | null = el;
  let depth = 0;
  while (cur && cur.nodeType === 1 && cur !== document.body && depth < 6) {
    const tag = cur.tagName.toLowerCase();
    const parent: HTMLElement | null = cur.parentElement;
    if (!parent) { segments.unshift(tag); break; }
    const curEl: Element = cur;
    const sameTag = Array.from(parent.children).filter((c: Element) => c.tagName === curEl.tagName);
    const idx = sameTag.indexOf(curEl) + 1;
    segments.unshift(`${tag}:nth-of-type(${idx})`);
    cur = parent;
    depth++;
  }
  return segments.join(' > ');
}

export default function ThemeEditorLegacy({ initialOverrides }: Props) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [selected, setSelected] = useState<HTMLElement | null>(null);
  const [selectorPath, setSelectorPath] = useState<string>('');
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [tab, setTab] = useState<Tab>('Color');
  const [editingBp, setEditingBp] = useState<BpKey>('base');
  const [showExport, setShowExport] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [overrides, setOverridesRaw] = useState<Overrides>(initialOverrides);
  const historyRef = useRef<{ stack: Overrides[]; idx: number }>({ stack: [initialOverrides], idx: 0 });
  const snapshotTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [, forceRerender] = useState(0);
  const HISTORY_MAX = 50;

  // setOverrides wrapper: aggiorna state + debounce snapshot in history
  const setOverrides = useCallback((updater: Overrides | ((prev: Overrides) => Overrides)) => {
    setOverridesRaw((prev) => {
      const next = typeof updater === 'function' ? (updater as (p: Overrides) => Overrides)(prev) : updater;
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
      snapshotTimerRef.current = setTimeout(() => {
        const h = historyRef.current;
        const truncated = h.stack.slice(0, h.idx + 1);
        truncated.push(JSON.parse(JSON.stringify(next)));
        if (truncated.length > HISTORY_MAX) truncated.shift();
        historyRef.current = { stack: truncated, idx: truncated.length - 1 };
        forceRerender((x) => x + 1);
      }, 300);
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.idx <= 0) return;
    h.idx -= 1;
    setOverridesRaw(JSON.parse(JSON.stringify(h.stack[h.idx])));
    forceRerender((x) => x + 1);
  }, []);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.idx >= h.stack.length - 1) return;
    h.idx += 1;
    setOverridesRaw(JSON.parse(JSON.stringify(h.stack[h.idx])));
    forceRerender((x) => x + 1);
  }, []);

  const canUndo = historyRef.current.idx > 0;
  const canRedo = historyRef.current.idx < historyRef.current.stack.length - 1;

  // Keyboard shortcuts complete
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Skip se sta digitando in input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
      const isCmd = e.metaKey || e.ctrlKey;

      // Cmd+Z undo / Cmd+Shift+Z redo
      if (isCmd && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (isCmd && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo(); return; }

      // Cmd+F focus search
      if (isCmd && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => document.getElementById('theme-editor-search-input')?.focus(), 50);
        return;
      }

      if (isTyping) return;

      // Lettera singola hotkeys (solo se non typing)
      const k = e.key.toLowerCase();
      if (k === 'p') { e.preventDefault(); setPickMode((v) => !v); }
      else if (k === 'v') { e.preventDefault(); setSelected(null); setSelectorPath(''); setRect(null); }
      else if (k === 'x') { e.preventDefault(); setShowExport(true); }
      else if (k === '/') { e.preventDefault(); setSearchOpen(true); setTimeout(() => document.getElementById('theme-editor-search-input')?.focus(), 50); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, undo, redo]);

  // Search: applica querySelector → seleziona primo match
  const runSearch = useCallback((q: string) => {
    if (!q.trim()) return;
    try {
      const found = document.querySelector(q) as HTMLElement | null;
      if (found && !found.closest('[data-theme-editor-ui]')) {
        setSelected(found);
        setSelectorPath(cssPath(found));
        setRect(found.getBoundingClientRect());
        found.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch { /* selettore invalido */ }
  }, []);
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState<HTMLElement | null>(null);
  const [vp, setVp] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  useEffect(() => {
    const update = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const breakpoint = useMemo(() => {
    const w = vp.w;
    if (w >= 1536) return '2xl';
    if (w >= 1280) return 'xl';
    if (w >= 1024) return 'lg';
    if (w >= 768) return 'md';
    if (w >= 640) return 'sm';
    return 'xs';
  }, [vp.w]);
  const styleTagRef = useRef<HTMLStyleElement | null>(null);
  const dragRef = useRef<null | {
    handle: 'nw'|'n'|'ne'|'w'|'e'|'sw'|'s'|'se';
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    startML: number;
    startMT: number;
  }>(null);

  useEffect(() => { setMounted(true); }, []);

  // Inject <style> tag for persistent CSS overrides
  useEffect(() => {
    if (!mounted) return;
    if (!styleTagRef.current) {
      const tag = document.createElement('style');
      tag.id = 'theme-editor-overrides';
      document.head.appendChild(tag);
      styleTagRef.current = tag;
    }
    // Genera CSS supportando nested per-breakpoint keys (@sm/@md/...)
    const baseRules: string[] = [];
    const mediaRules: Record<string, string[]> = {};
    for (const [sel, props] of Object.entries(overrides)) {
      const baseProps: string[] = [];
      for (const [k, v] of Object.entries(props)) {
        if (k.startsWith('@') && typeof v === 'object') {
          const bp = k.slice(1) as Exclude<BpKey, 'base'>;
          if (BP_MIN[bp]) {
            const body = Object.entries(v as Record<string, string>).map(([pk, pv]) => `    ${pk}: ${pv} !important;`).join('\n');
            const rule = `  ${sel} {\n${body}\n  }`;
            (mediaRules[bp] ||= []).push(rule);
          }
        } else if (typeof v === 'string') {
          baseProps.push(`  ${k}: ${v} !important;`);
        }
      }
      if (baseProps.length) baseRules.push(`${sel} {\n${baseProps.join('\n')}\n}`);
    }
    let css = baseRules.join('\n\n');
    for (const bp of Object.keys(mediaRules) as Exclude<BpKey, 'base'>[]) {
      css += `\n\n@media (min-width: ${BP_MIN[bp]}px) {\n${mediaRules[bp].join('\n\n')}\n}`;
    }
    styleTagRef.current.textContent = css;
  }, [overrides, mounted]);

  // Pick mode handlers
  useEffect(() => {
    if (!pickMode) {
      setHover(null);
      return;
    }
    const onMove = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el || el.closest('[data-theme-editor-ui]')) return;
      setHover(el);
    };
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el || el.closest('[data-theme-editor-ui]')) return;
      e.preventDefault();
      e.stopPropagation();
      setSelected(el);
      setSelectorPath(cssPath(el));
      setRect(el.getBoundingClientRect());
      setPickMode(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickMode(false);
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onEsc);
    };
  }, [pickMode]);

  // Track rect of selected element (resize/scroll updates)
  useEffect(() => {
    if (!selected) return;
    const update = () => setRect(selected.getBoundingClientRect());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [selected]);

  const setProp = useCallback((prop: string, value: string) => {
    if (!selectorPath) return;
    setOverrides((prev) => {
      const next = { ...prev };
      const cur = { ...(next[selectorPath] || {}) };
      if (editingBp === 'base') {
        if (value) cur[prop] = value;
        else delete cur[prop];
      } else {
        const bpKey = `@${editingBp}`;
        const bpRec = { ...((cur[bpKey] as Record<string, string>) || {}) };
        if (value) bpRec[prop] = value;
        else delete bpRec[prop];
        if (Object.keys(bpRec).length === 0) delete cur[bpKey];
        else cur[bpKey] = bpRec;
      }
      if (Object.keys(cur).length === 0) delete next[selectorPath];
      else next[selectorPath] = cur;
      return next;
    });
  }, [selectorPath, editingBp, setOverrides]);

  const currentProps = useMemo(() => {
    const sel = overrides[selectorPath] || {};
    if (editingBp === 'base') {
      // Filtra fuori chiavi @bp (sono record nested, non props)
      const flat: Record<string, string> = {};
      for (const [k, v] of Object.entries(sel)) if (typeof v === 'string') flat[k] = v;
      return flat;
    }
    return ((sel[`@${editingBp}`] as Record<string, string>) || {});
  }, [overrides, selectorPath, editingBp]);

  // Drag-resize 8 handles
  const onHandleDown = useCallback((handle: 'nw'|'n'|'ne'|'w'|'e'|'sw'|'s'|'se') => (e: React.PointerEvent) => {
    if (!selected) return;
    e.preventDefault();
    e.stopPropagation();
    const r = selected.getBoundingClientRect();
    const cs = window.getComputedStyle(selected);
    dragRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startW: r.width,
      startH: r.height,
      startML: parseFloat(cs.marginLeft) || 0,
      startMT: parseFloat(cs.marginTop) || 0,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [selected]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || !selectorPath) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      let nw = d.startW, nh = d.startH, nml = d.startML, nmt = d.startMT;
      const h = d.handle;
      if (h.includes('e')) nw = Math.max(8, d.startW + dx);
      if (h.includes('w')) { nw = Math.max(8, d.startW - dx); nml = d.startML + dx; }
      if (h.includes('s')) nh = Math.max(8, d.startH + dy);
      if (h.includes('n')) { nh = Math.max(8, d.startH - dy); nmt = d.startMT + dy; }
      setOverrides((prev) => {
        const next = { ...prev };
        const cur = { ...(next[selectorPath] || {}) };
        if (h.includes('e') || h.includes('w')) cur['width'] = `${Math.round(nw)}px`;
        if (h.includes('s') || h.includes('n')) cur['height'] = `${Math.round(nh)}px`;
        if (h.includes('w')) cur['margin-left'] = `${Math.round(nml)}px`;
        if (h.includes('n')) cur['margin-top'] = `${Math.round(nmt)}px`;
        next[selectorPath] = cur;
        return next;
      });
    };
    const onUp = () => { dragRef.current = null; };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [selectorPath]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/theme-editor/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides }),
      });
      if (!res.ok) throw new Error('save failed');
    } finally {
      setSaving(false);
    }
  }, [overrides]);

  if (!mounted) return null;

  return createPortal(
    <div data-theme-editor-ui>
      {/* Floating toggle button bottom-left */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-cid="theme-editor-toggle"
        title="Theme Editor"
        className="fixed bottom-6 left-6 z-[2147483646] inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-900 text-white shadow-2xl hover:scale-105 active:scale-95 transition-transform"
        style={{ fontSize: 22 }}
      >
        🎨
      </button>

      {open && (
        <>
          {/* Hover highlight pick mode */}
          {pickMode && hover && (() => {
            const r = hover.getBoundingClientRect();
            return (
              <div
                className="fixed pointer-events-none z-[2147483640]"
                style={{
                  top: r.top, left: r.left, width: r.width, height: r.height,
                  outline: '2px dashed rgba(6,182,212,0.9)',
                  background: 'rgba(6,182,212,0.08)',
                }}
              />
            );
          })()}

          {/* Spacing visualizer (margin/padding overlay Figma-style) */}
          {selected && rect && (() => {
            const cs = window.getComputedStyle(selected);
            const pt = parseFloat(cs.paddingTop) || 0;
            const pr = parseFloat(cs.paddingRight) || 0;
            const pb = parseFloat(cs.paddingBottom) || 0;
            const pl = parseFloat(cs.paddingLeft) || 0;
            const mt = parseFloat(cs.marginTop) || 0;
            const mr = parseFloat(cs.marginRight) || 0;
            const mb = parseFloat(cs.marginBottom) || 0;
            const ml = parseFloat(cs.marginLeft) || 0;
            return (
              <>
                {/* Margin overlay (arancio fuori) */}
                {mt > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-orange-400/25" style={{ top: rect.top - mt, left: rect.left - ml, width: rect.width + ml + mr, height: mt }} />
                )}
                {mb > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-orange-400/25" style={{ top: rect.bottom, left: rect.left - ml, width: rect.width + ml + mr, height: mb }} />
                )}
                {ml > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-orange-400/25" style={{ top: rect.top, left: rect.left - ml, width: ml, height: rect.height }} />
                )}
                {mr > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-orange-400/25" style={{ top: rect.top, left: rect.right, width: mr, height: rect.height }} />
                )}
                {/* Padding overlay (verde dentro) */}
                {pt > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-emerald-400/25" style={{ top: rect.top, left: rect.left, width: rect.width, height: pt }} />
                )}
                {pb > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-emerald-400/25" style={{ top: rect.bottom - pb, left: rect.left, width: rect.width, height: pb }} />
                )}
                {pl > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-emerald-400/25" style={{ top: rect.top + pt, left: rect.left, width: pl, height: rect.height - pt - pb }} />
                )}
                {pr > 0 && (
                  <div className="fixed pointer-events-none z-[2147483639] bg-emerald-400/25" style={{ top: rect.top + pt, left: rect.right - pr, width: pr, height: rect.height - pt - pb }} />
                )}
              </>
            );
          })()}

          {/* Selected bounding box + 8 drag handles */}
          {selected && rect && (
            <div
              className="fixed pointer-events-none z-[2147483641]"
              style={{
                top: rect.top, left: rect.left, width: rect.width, height: rect.height,
                outline: '2px solid #06b6d4',
                boxShadow: '0 0 0 1px rgba(6,182,212,0.3) inset',
              }}
            >
              <div className="absolute -top-6 left-0 text-[10px] font-bold text-white bg-cyan-600 px-2 py-0.5 rounded">
                {Math.round(rect.width)} × {Math.round(rect.height)} — {selectorPath.slice(0, 50)}
              </div>
              {/* 8 handles drag (pointer-events-auto, sopra l'outline) */}
              {([
                ['nw', '-top-1.5 -left-1.5 cursor-nwse-resize'],
                ['n',  '-top-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize'],
                ['ne', '-top-1.5 -right-1.5 cursor-nesw-resize'],
                ['w',  'top-1/2 -translate-y-1/2 -left-1.5 cursor-ew-resize'],
                ['e',  'top-1/2 -translate-y-1/2 -right-1.5 cursor-ew-resize'],
                ['sw', '-bottom-1.5 -left-1.5 cursor-nesw-resize'],
                ['s',  '-bottom-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize'],
                ['se', '-bottom-1.5 -right-1.5 cursor-nwse-resize'],
              ] as const).map(([dir, pos]) => (
                <div
                  key={dir}
                  data-theme-editor-ui
                  onPointerDown={onHandleDown(dir)}
                  className={`absolute w-3 h-3 rounded-sm bg-white border border-cyan-600 shadow pointer-events-auto ${pos}`}
                  title={`Resize ${dir}`}
                />
              ))}
            </div>
          )}

          {/* Search bar (top, sotto toolbar) */}
          {searchOpen && (
            <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[2147483644] inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-2 rounded-xl shadow-2xl">
              <input
                id="theme-editor-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch(searchQuery);
                  if (e.key === 'Escape') setSearchOpen(false);
                }}
                placeholder='Selettore CSS — es. [data-cid="hero"] o #excursion-section'
                className="w-96 px-3 py-1.5 rounded-lg bg-slate-800 text-white placeholder:text-slate-400 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-cyan-400"
                autoFocus
              />
              <button
                type="button"
                onClick={() => runSearch(searchQuery)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-cyan-500 hover:bg-cyan-400 text-slate-900 transition-colors"
              >
                Cerca
              </button>
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-700 hover:bg-slate-600 transition-colors"
              >
                ✕
              </button>
            </div>
          )}

          {/* Viewport indicator (top-right) */}
          <div className="fixed top-4 right-4 z-[2147483642] inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-1.5 rounded-lg shadow-2xl text-[11px] font-mono">
            <span className="text-cyan-400 font-bold">{vp.w}×{vp.h}</span>
            <span className="text-slate-400">·</span>
            <span className={`px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
              breakpoint === '2xl' ? 'bg-purple-500/30 text-purple-300' :
              breakpoint === 'xl' ? 'bg-blue-500/30 text-blue-300' :
              breakpoint === 'lg' ? 'bg-emerald-500/30 text-emerald-300' :
              breakpoint === 'md' ? 'bg-amber-500/30 text-amber-300' :
              breakpoint === 'sm' ? 'bg-orange-500/30 text-orange-300' :
              'bg-rose-500/30 text-rose-300'
            }`}>{breakpoint}</span>
          </div>

          {/* Top toolbar */}
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[2147483642] inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-2 rounded-xl shadow-2xl">
            <button
              type="button"
              onClick={() => setPickMode((v) => !v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                pickMode ? 'bg-cyan-500 text-slate-900' : 'bg-slate-700 hover:bg-slate-600'
              }`}
            >
              {pickMode ? '◉ Pick mode' : 'Pick'}
            </button>
            <button
              type="button"
              onClick={() => { setSelected(null); setSelectorPath(''); setRect(null); }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-700 hover:bg-slate-600 transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Cmd/Ctrl+Z)"
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-700 hover:bg-slate-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ↶ Undo
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Cmd/Ctrl+Shift+Z)"
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-700 hover:bg-slate-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ↷ Redo
            </button>
            <button
              type="button"
              onClick={() => { setOverrides({}); }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-rose-600 hover:bg-rose-500 transition-colors"
            >
              Reset all
            </button>
            <button
              type="button"
              onClick={() => { setSearchOpen(true); setTimeout(() => document.getElementById('theme-editor-search-input')?.focus(), 50); }}
              title="Search (/ or Cmd+F)"
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-700 hover:bg-slate-600 transition-colors"
            >
              🔍 Search
            </button>
            <button
              type="button"
              onClick={() => setShowExport(true)}
              title="Export Tailwind classes"
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-indigo-500 hover:bg-indigo-400 text-white transition-colors"
            >
              📤 Export
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-emerald-500 hover:bg-emerald-400 text-slate-900 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvataggio…' : '💾 Salva'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-700 hover:bg-slate-600 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Side panel destra */}
          {selected && (
            <div className="fixed right-4 top-20 bottom-4 w-80 z-[2147483643] bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 flex flex-col overflow-hidden">
              {/* Breadcrumb ancestors */}
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 overflow-x-auto premium-snap-noscrollbar">
                <div className="flex items-center gap-1 text-[10px] whitespace-nowrap">
                  {getAncestors(selected).map((anc, i, arr) => (
                    <span key={i} className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(anc);
                          setSelectorPath(cssPath(anc));
                          setRect(anc.getBoundingClientRect());
                        }}
                        className={`px-1.5 py-0.5 rounded font-mono transition-colors ${
                          anc === selected
                            ? 'bg-cyan-100 text-cyan-800 font-bold'
                            : 'text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {ancestorLabel(anc)}
                      </button>
                      {i < arr.length - 1 && <span className="text-slate-400">›</span>}
                    </span>
                  ))}
                </div>
              </div>
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Selettore</div>
                <div className="text-xs font-mono text-slate-900 truncate" title={selectorPath}>{selectorPath}</div>
              </div>
              {/* Breakpoint scope chips */}
              <div className="px-3 py-2 border-b border-slate-200 bg-white">
                <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Edit scope</div>
                <div className="flex gap-1">
                  {(['base', 'sm', 'md', 'lg', 'xl', '2xl'] as BpKey[]).map((bp) => (
                    <button
                      key={bp}
                      type="button"
                      onClick={() => setEditingBp(bp)}
                      className={`flex-1 px-1 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
                        editingBp === bp
                          ? 'bg-cyan-500 text-white'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                      title={bp === 'base' ? 'Tutti i breakpoint' : `${bp}+ (min-width: ${BP_MIN[bp as Exclude<BpKey, 'base'>]}px)`}
                    >
                      {bp === 'base' ? 'all' : bp}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider">
                {PANEL_TABS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`flex-1 px-2 py-2 transition-colors ${
                      tab === t ? 'bg-cyan-50 text-cyan-700 border-b-2 border-cyan-500' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
                {tab === 'Size' && (
                  <>
                    <PropInput label="width" value={currentProps['width'] || ''} onChange={(v) => setProp('width', v)} placeholder="auto / 320px / 50%" />
                    <PropInput label="height" value={currentProps['height'] || ''} onChange={(v) => setProp('height', v)} placeholder="auto / 200px" />
                    <PropInput label="max-width" value={currentProps['max-width'] || ''} onChange={(v) => setProp('max-width', v)} placeholder="none / 720px" />
                    <PropInput label="min-height" value={currentProps['min-height'] || ''} onChange={(v) => setProp('min-height', v)} placeholder="0 / 100svh" />
                  </>
                )}
                {tab === 'Spacing' && (
                  <>
                    <PropInput label="padding" value={currentProps['padding'] || ''} onChange={(v) => setProp('padding', v)} placeholder="16px / 1rem 2rem" />
                    <PropInput label="margin" value={currentProps['margin'] || ''} onChange={(v) => setProp('margin', v)} placeholder="0 / 1rem auto" />
                    <PropInput label="gap" value={currentProps['gap'] || ''} onChange={(v) => setProp('gap', v)} placeholder="8px / 1rem" />
                  </>
                )}
                {tab === 'Color' && (
                  <>
                    <ColorRow label="background" value={currentProps['background'] || ''} onChange={(v) => setProp('background', v)} />
                    <ColorRow label="color" value={currentProps['color'] || ''} onChange={(v) => setProp('color', v)} />
                    <ColorRow label="border-color" value={currentProps['border-color'] || ''} onChange={(v) => setProp('border-color', v)} />
                    <PropInput label="border-radius" value={currentProps['border-radius'] || ''} onChange={(v) => setProp('border-radius', v)} placeholder="0 / 8px / 9999px" />
                    <PropInput label="box-shadow" value={currentProps['box-shadow'] || ''} onChange={(v) => setProp('box-shadow', v)} placeholder="0 4px 12px rgba(...)" />
                  </>
                )}
                {tab === 'Typography' && (
                  <>
                    <PropInput label="font-size" value={currentProps['font-size'] || ''} onChange={(v) => setProp('font-size', v)} placeholder="14px / 1rem / clamp(...)" />
                    <PropInput label="font-weight" value={currentProps['font-weight'] || ''} onChange={(v) => setProp('font-weight', v)} placeholder="400 / 700 / 900" />
                    <PropInput label="line-height" value={currentProps['line-height'] || ''} onChange={(v) => setProp('line-height', v)} placeholder="1.5 / 1.2" />
                    <PropInput label="letter-spacing" value={currentProps['letter-spacing'] || ''} onChange={(v) => setProp('letter-spacing', v)} placeholder="normal / 0.05em" />
                    <PropInput label="text-align" value={currentProps['text-align'] || ''} onChange={(v) => setProp('text-align', v)} placeholder="left / center / right" />
                  </>
                )}
                {tab === 'Attrs' && selected && (
                  <>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tag</div>
                    <div className="text-xs font-mono text-slate-900 mb-3">{selected.tagName.toLowerCase()}</div>
                    <AttrInput
                      label="data-cid"
                      value={selected.getAttribute('data-cid') ?? ''}
                      placeholder="es. hero-image-card"
                      onChange={(next) => {
                        const old = selected.getAttribute('data-cid');
                        if (next) selected.setAttribute('data-cid', next);
                        else selected.removeAttribute('data-cid');
                        const oldSel = old ? `[data-cid="${old}"]` : selectorPath;
                        const newSel = next ? `[data-cid="${next}"]` : (selected.id ? `#${selected.id}` : cssPath(selected));
                        if (oldSel !== newSel) {
                          setOverrides((prev) => {
                            if (!prev[oldSel]) return prev;
                            const cp = { ...prev };
                            cp[newSel] = cp[oldSel];
                            delete cp[oldSel];
                            return cp;
                          });
                          setSelectorPath(newSel);
                        }
                      }}
                      onCopy={selected.getAttribute('data-cid') ?? ''}
                    />
                    <AttrInput
                      label="id"
                      value={selected.id}
                      placeholder="es. excursion-hero"
                      onChange={(next) => {
                        if (next) selected.setAttribute('id', next);
                        else selected.removeAttribute('id');
                        if (!selected.getAttribute('data-cid')) {
                          const newSel = next ? `#${next}` : cssPath(selected);
                          if (newSel !== selectorPath) {
                            setOverrides((prev) => {
                              if (!prev[selectorPath]) return prev;
                              const cp = { ...prev };
                              cp[newSel] = cp[selectorPath];
                              delete cp[selectorPath];
                              return cp;
                            });
                            setSelectorPath(newSel);
                          }
                        }
                      }}
                      onCopy={selected.id}
                    />
                    <div className="pt-2 border-t border-slate-200">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">className (read-only)</div>
                      <div className="text-[11px] font-mono text-slate-600 break-all bg-slate-50 rounded p-2 max-h-24 overflow-y-auto">
                        {selected.className || '—'}
                      </div>
                    </div>
                  </>
                )}
                {tab === 'Raw' && (
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">CSS raw key:value (1 per riga)</div>
                    <textarea
                      value={Object.entries(currentProps).map(([k, v]) => `${k}: ${v}`).join('\n')}
                      onChange={(e) => {
                        const lines = e.target.value.split('\n');
                        const next: Record<string, string> = {};
                        for (const line of lines) {
                          const idx = line.indexOf(':');
                          if (idx === -1) continue;
                          const k = line.slice(0, idx).trim();
                          const v = line.slice(idx + 1).trim();
                          if (k && v) next[k] = v;
                        }
                        setOverrides((prev) => {
                          const cp = { ...prev };
                          if (Object.keys(next).length === 0) delete cp[selectorPath];
                          else cp[selectorPath] = next;
                          return cp;
                        });
                      }}
                      className="w-full h-48 px-3 py-2 text-xs font-mono rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          {showExport && (() => {
            const exportText = generateExport(overrides);
            return (
              <div
                className="fixed inset-0 z-[2147483644] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
                onClick={() => setShowExport(false)}
              >
                <div
                  className="relative w-full max-w-3xl max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <div className="text-base font-black text-slate-900">Export Tailwind</div>
                      <div className="text-xs text-slate-500">Classi pronte da incollare nei JSX. Selettore in commento sopra.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(exportText)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-indigo-500 hover:bg-indigo-400 text-white transition-colors"
                    >
                      📋 Copy all
                    </button>
                  </div>
                  <textarea
                    readOnly
                    value={exportText}
                    className="flex-1 p-4 text-xs font-mono text-slate-800 bg-slate-50 focus:outline-none resize-none"
                  />
                  <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowExport(false)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-slate-200 hover:bg-slate-300 text-slate-700 transition-colors"
                    >
                      Chiudi
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>,
    document.body,
  );
}

function PropInput({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400"
      />
    </label>
  );
}

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const isHex = /^#[0-9a-f]{3,8}$/i.test(value);
  const eyeDropperAvailable = typeof window !== 'undefined' && 'EyeDropper' in window;

  const pickColor = async () => {
    try {
      // EyeDropper API — Chromium 95+. Apre cursor magnifier + click pixel.
      const W = window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } };
      const ed = new W.EyeDropper();
      const result = await ed.open();
      onChange(result.sRGBHex);
    } catch {
      /* user cancelled o non supportato */
    }
  };

  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex gap-2">
        <input
          type="color"
          value={isHex ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 rounded-lg border border-slate-200 cursor-pointer"
        />
        {eyeDropperAvailable && (
          <button
            type="button"
            onClick={pickColor}
            title="Pick color from page (EyeDropper)"
            className="w-10 h-9 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 inline-flex items-center justify-center text-base"
          >
            🎯
          </button>
        )}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#ffffff / rgba(...) / transparent"
          className="flex-1 px-2.5 py-1.5 rounded-lg text-xs font-mono border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400"
        />
      </div>
    </label>
  );
}

function AttrInput({ label, value, placeholder, onChange, onCopy }: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onCopy?: string;
}) {
  return (
    <label className="block mb-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between">
        <span>{label}</span>
        {onCopy && (
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(onCopy)}
            className="text-[9px] text-cyan-700 hover:text-cyan-900 font-bold uppercase tracking-wider"
            title="Copia negli appunti"
          >
            📋 Copy
          </button>
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 rounded-lg text-xs font-mono border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400"
      />
    </label>
  );
}
